/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Serializes a filter object into a URL-safe base64url string.
 *
 * Exported because deep links are built outside this hook -- a card on
 * another screen that points at a pre-filtered list has to write the same
 * `f` param the hook reads, and a second copy of this encoding somewhere
 * else is a copy that drifts. Callers pair it with `?f=`.
 *
 * @param obj Filter key/value map to encode.
 * @returns Base64url-encoded JSON with padding stripped.
 */
export function encodeFilters(obj: Record<string, unknown>): string {
  const json = JSON.stringify(obj);
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Parses a base64url string produced by {@link encodeFilters} back into a
 * filter object.
 *
 * @param encoded Base64url-encoded JSON string.
 * @returns The decoded filter key/value map.
 */
function decodeFilters(encoded: string): Record<string, unknown> {
  const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(padded));
}

/**
 * Determines whether both operands are empty arrays.
 *
 * @param value Current filter value.
 * @param defaultValue The default for that filter key.
 * @returns `true` when both are arrays and both are empty.
 */
function areBothEmptyArrays(value: unknown, defaultValue: unknown): boolean {
  if (!Array.isArray(value) || !Array.isArray(defaultValue)) return false;
  return value.length === 0 && defaultValue.length === 0;
}

/**
 * Determines whether a filter value equals its default and can be omitted from
 * the URL (empty strings, undefined, and empty arrays count as default).
 *
 * @param value Current filter value.
 * @param defaultValue The default for that filter key.
 * @returns `true` when the value should not be persisted.
 */
function isDefault(value: unknown, defaultValue: unknown): boolean {
  if (value === defaultValue) return true;
  if (value === '' || value === undefined) return true;
  return areBothEmptyArrays(value, defaultValue);
}

/**
 * Writes a full filter map into the `f` param of a `URLSearchParams`, stripping
 * every key whose value equals its default so the URL stays clean. Mutates and
 * returns the given params for easy chaining.
 *
 * This is the single encoding path shared by {@link useUrlFilters} and by the
 * combined grouping-exit updater, so neither can drift from the other's idea of
 * what "an empty filter" encodes to.
 *
 * @param params The params to mutate (the `f` key is set or deleted).
 * @param merged The complete filter map (current filters with the change
 *   already applied).
 * @param defaults The full set of filter keys with their default values.
 * @returns The same `params`, with `f` written or removed.
 */
export function writeFiltersParam<T extends Record<string, unknown>>(
  params: URLSearchParams,
  merged: T,
  defaults: T,
): URLSearchParams {
  const nonDefault = Object.fromEntries(
    Object.entries(merged).filter(([k, v]) => !isDefault(v, defaults[k])),
  );
  if (Object.keys(nonDefault).length === 0) params.delete('f');
  else params.set('f', encodeFilters(nonDefault));
  return params;
}

/**
 * Reads the `f` filter map out of a `URLSearchParams`, merged over the defaults.
 *
 * Exported so a caller that composes a filter change with OTHER param writes in
 * one atomic `setSearchParams` can read the current filters without a second
 * hook instance (two hooks each writing in the same tick is the race this
 * exists to avoid).
 *
 * @param params The search params to read `f` from.
 * @param defaults The full set of filter keys with their default values.
 * @returns The stored filters merged over the defaults; the defaults alone when
 *   the param is absent or cannot be decoded.
 */
export function readFiltersParam<T extends Record<string, unknown>>(
  params: URLSearchParams,
  defaults: T,
): T {
  return readFilters(params, defaults);
}

/**
 * Persists filter state in a single URL search param (`f`) as base64url-encoded
 * JSON. Only non-default values are stored; empty filters produce a clean URL.
 *
 * Because every key shares one param, `setFilters` must be called ONCE per
 * change, with every key that change touches. Two calls in the same tick do
 * not compose -- react-router does not queue `setSearchParams` -- and the
 * second one overwrites the first from the pre-update state, which reads as
 * "the filter silently did nothing".
 *
 * @param defaults The full set of filter keys with their default values.
 * @returns The current `filters`, a `setFilters` partial updater, and a
 * `resetFilters` callback that clears the param.
 */
/**
 * Reads the filter state out of a `URLSearchParams` rather than out of a
 * React closure.
 *
 * @param params The search params to read `f` from.
 * @param defaults The full set of filter keys with their default values.
 * @returns The stored filters merged over the defaults; the defaults alone
 * when the param is absent or cannot be decoded.
 */
function readFilters<T extends Record<string, unknown>>(
  params: URLSearchParams,
  defaults: T,
): T {
  const raw = params.get('f');
  if (!raw) return defaults;
  try {
    return { ...defaults, ...decodeFilters(raw) } as T;
  } catch {
    return defaults;
  }
}

/**
 * Use url filters.
 */
export function useUrlFilters<T extends Record<string, unknown>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The live search params, readable from inside a callback that was created
   * several renders ago.
   *
   * Neither `searchParams` nor the `prev` handed to a `setSearchParams`
   * updater is safe on its own. React Router builds `setSearchParams` with
   * `useCallback([navigate, searchParams])` and calls the updater as
   * `nextInit(new URLSearchParams(searchParams))` -- so an older callback
   * carries an older `setSearchParams`, whose `prev` is that old render's
   * params. Merging against either one lets a stale caller write its snapshot
   * back over the URL and drop whatever was set in between, with no error.
   * A ref is shared by every closure over this hook instance, so it always
   * holds the latest params no matter which callback generation runs.
   */
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  const filters: T = useMemo(
    () => readFilters(searchParams, defaults),
    [searchParams, defaults],
  );

  const setFilters = useCallback(
    (partial: Partial<T>) => {
      // `prev` is deliberately ignored in favour of the ref, for the reason
      // above. What this does NOT buy is composing two calls in the same
      // tick: react-router documents that "multiple calls to setSearchParams
      // in the same tick will not build on the prior value", and no render
      // happens between them, so the ref has not moved either. Apply a whole
      // filter change in ONE call with every key it touches.
      setSearchParams(() => {
        const next = new URLSearchParams(paramsRef.current);
        const merged = { ...readFilters(paramsRef.current, defaults), ...partial };
        return writeFiltersParam(next, merged, defaults);
      }, { replace: true });
    },
    [setSearchParams, defaults],
  );

  const resetFilters = useCallback(() => {
    setSearchParams(() => {
      const n = new URLSearchParams(paramsRef.current);
      n.delete('f');
      return n;
    }, { replace: true });
  }, [setSearchParams]);

  return { filters, setFilters, resetFilters };
}
